import {apiDelete, apiGet, apiPost, apiPut} from './client'
import {bareEvent, createObject, eventRef, formatDate, parseDate} from './edit'
import {famsOf} from './tree'
import type {Family, TreeIndex} from './types'

/* Ни handle, ни gramps_id клиент не придумывает: сервер назначает их сам
 * (проверено — POST без них возвращает 201 и присвоенный номер). Генерация
 * номеров на клиенте давала «Error while adding object» на занятом номере
 * и в принципе не работает, когда правят несколько человек сразу.
 *
 * Родство правим ТОЛЬКО через /api/families/ — POST зовёт add_family_update_refs,
 * PUT зовёт update_family_update_refs, и оба чинят обратные ссылки в персонах
 * (family_list у супругов, parent_family_list у детей). Сырые транзакции этого
 * не делают: там пришлось бы синхронизировать обе стороны вручную, а это ровно
 * то место, где древо портится незаметно.
 *
 * Взамен теряем атомарность с правкой самого человека — но рассинхрон связей
 * дороже двух отдельных запросов. */

type RawFamily = Record<string, unknown> & {
  handle: string
  father_handle?: string | null
  mother_handle?: string | null
  child_ref_list?: {ref: string; [k: string]: unknown}[]
}

const childRef = (ref: string) => ({
  _class: 'ChildRef',
  ref,
  private: false,
  citation_list: [],
  note_list: [],
  frel: 'Birth',
  mrel: 'Birth',
})

/** Семья, в которой человек — ребёнок. */
const parentFamily = (ix: TreeIndex, h: string): Family | undefined => ix.famOfChild.get(h)

/** Задать/сменить/убрать родителей. Пустые значения допустимы: у половины
 *  древа известен только один родитель. */
export async function setParents(
  ix: TreeIndex,
  person: string,
  father: string | null,
  mother: string | null,
): Promise<void> {
  const fam = parentFamily(ix, person)

  if (fam) {
    const full = await apiGet<RawFamily>(`/families/${fam.handle}`)
    if ((full.father_handle || null) === father && (full.mother_handle || null) === mother) return
    await apiPut(`/families/${fam.handle}`, {...full, father_handle: father, mother_handle: mother})
    return
  }
  if (!father && !mother) return

  await apiPost('/families/', {
    _class: 'Family',
    father_handle: father,
    mother_handle: mother,
    child_ref_list: [childRef(person)],
  })
}

/** Задать/сменить/убрать супруга. Сторона (отец/мать) выбирается по полу
 *  самого человека — в Gramps у семьи именно father_handle и mother_handle. */
export async function setSpouse(
  ix: TreeIndex,
  person: string,
  spouse: string | null,
): Promise<void> {
  const fam = famsOf(ix, person)[0]

  if (fam) {
    const full = await apiGet<RawFamily>(`/families/${fam.handle}`)
    const personIsFather = full.father_handle === person
    const next = personIsFather
      ? {...full, mother_handle: spouse}
      : {...full, father_handle: spouse}
    const before = personIsFather ? full.mother_handle : full.father_handle
    if ((before || null) === spouse) return
    await apiPut(`/families/${fam.handle}`, next)
    return
  }
  if (!spouse) return

  const male = ix.people.get(person)?.gender === 1
  await apiPost('/families/', {
    _class: 'Family',
    father_handle: male ? person : spouse,
    mother_handle: male ? spouse : person,
    child_ref_list: [],
  })
}

/** Добавить существующего человека ребёнком. Если своей семьи ещё нет —
 *  заводим её (возможно, с одним родителем). */
export async function addChild(ix: TreeIndex, parent: string, child: string): Promise<void> {
  if (parent === child) throw new Error('Человек не может быть своим ребёнком')
  const fam = famsOf(ix, parent)[0]

  if (fam) {
    const full = await apiGet<RawFamily>(`/families/${fam.handle}`)
    const kids = full.child_ref_list || []
    if (kids.some(r => r.ref === child)) return
    await apiPut(`/families/${fam.handle}`, {...full, child_ref_list: [...kids, childRef(child)]})
    return
  }
  const male = ix.people.get(parent)?.gender === 1
  await apiPost('/families/', {
    _class: 'Family',
    father_handle: male ? parent : null,
    mother_handle: male ? null : parent,
    child_ref_list: [childRef(child)],
  })
}

/** Убрать ребёнка из семьи. Саму семью не удаляем — это отдельное решение,
 *  и пустая семья безопаснее случайного удаления связей. */
export async function removeChild(ix: TreeIndex, parent: string, child: string): Promise<void> {
  const fam = famsOf(ix, parent).find(f => (f.child_ref_list || []).some(r => r.ref === child))
  if (!fam) return
  const full = await apiGet<RawFamily>(`/families/${fam.handle}`)
  await apiPut(`/families/${fam.handle}`, {
    ...full,
    child_ref_list: (full.child_ref_list || []).filter(r => r.ref !== child),
  })
}

/* ---------- создание родственника прямо из древа ---------- */

export type NewRole = 'son' | 'daughter' | 'father' | 'mother' | 'spouse' | 'brother' | 'sister'

/** Добавить ребёнка в конкретную семью — не в «первую попавшуюся».
 *  Для брата и сестры это принципиально: у человека может быть отчим,
 *  и вторая семья родителя — не та, где он ребёнок. */
export async function addToFamily(famHandle: string, child: string): Promise<void> {
  const full = await apiGet<RawFamily>(`/families/${famHandle}`)
  const kids = full.child_ref_list || []
  if (kids.some(r => r.ref === child)) return
  await apiPut(`/families/${famHandle}`, {...full, child_ref_list: [...kids, childRef(child)]})
}

/**
 * Привязать только что созданного человека к тому, от кого его заводили.
 *
 * Индекс здесь заведомо устаревший — нового человека в нём ещё нет. Поэтому
 * все правки идут от уже существующего родственника: у него семьи известны.
 * Для брата и сестры нельзя звать setParents на новичке — он не найдёт его
 * родительскую семью в индексе и заведёт вторую такую же.
 */
export async function attachNew(
  ix: TreeIndex,
  role: NewRole,
  to: string,
  created: string,
  /** в какую именно семью — у человека их может быть несколько, и
   *  «первая попавшаяся» отправила бы ребёнка не в тот брак */
  family?: string,
): Promise<void> {
  const born = ix.famOfChild.get(to)
  switch (role) {
    case 'son':
    case 'daughter':
      return family ? addToFamily(family, created) : addChild(ix, to, created)
    case 'father':
      return setParents(ix, to, created, born?.mother_handle ?? null)
    case 'mother':
      return setParents(ix, to, born?.father_handle ?? null, created)
    case 'spouse':
      return setSpouse(ix, to, created)
    case 'brother':
    case 'sister':
      if (!born) throw new Error('У человека не указаны родители — сначала добавьте их')
      return addToFamily(born.handle, created)
  }
}

/**
 * Удалить человека вместе со связями.
 *
 * DELETE /people/ зовёт родной delete_person_from_database Gramps: он
 * вычищает человека из всех семей — и как супруга, и как ребёнка — и
 * убирает семьи, в которых после этого никого не осталось. Событий,
 * которые были только у него, это не касается: они остаются в базе
 * ничьими, зато ничего не пропадёт по ошибке.
 */
export async function deletePerson(handle: string): Promise<void> {
  await apiDelete(`/people/${handle}`)
}

/* ---------- семьи человека ----------
 *
 * В Gramps у человека не «супруг», а СПИСОК семей: второй брак — это
 * вторая семья со своими детьми и своей датой свадьбы. Раньше мы брали
 * famsOf(...)[0] и правили только её; всё, что было во второй, из
 * интерфейса не показывалось и не правилось, хотя лежало в базе.
 *
 * Дети принадлежат семье, а не человеку — поэтому и правятся внутри
 * своей семьи: иначе на вопрос «чьи это дети и от какого брака»
 * интерфейс ответить не может. */

export interface Union {
  /** handle семьи; null — семьи ещё нет, заведём при сохранении */
  family: string | null
  spouse: string | null
  children: string[]
  wedDate: string
  wedPlace: string | null
  /* Развод. Семью он не распускает: супруги в ней остаются, иначе вместе
     со связью пропало бы родительство детей. */
  divDate: string
  divPlace: string | null
}

/** Все семьи человека как партнёра — из уже загруженного индекса. */
export function unionsOf(ix: TreeIndex, person: string): Union[] {
  return famsOf(ix, person).map(f => {
    const evs = (f.event_ref_list || []).map(r => ix.events.get(r.ref))
    const wed = evs.find(e => e && isFamilyEvent(e.type, EVENT_MARRIAGE))
    const div = evs.find(e => e && isFamilyEvent(e.type, EVENT_DIVORCE))
    return {
      family: f.handle,
      spouse: (f.father_handle === person ? f.mother_handle : f.father_handle) || null,
      children: (f.child_ref_list || []).map(r => r.ref),
      wedDate: formatDate(wed?.date?.dateval),
      wedPlace: wed?.place || null,
      divDate: formatDate(div?.date?.dateval),
      divPlace: div?.place || null,
    }
  })
}

export const emptyUnion = (): Union => ({
  family: null, spouse: null, children: [],
  wedDate: '', wedPlace: null, divDate: '', divPlace: null,
})

const unionIsEmpty = (u: Union) =>
  !u.spouse && !u.children.length && !u.wedDate && !u.wedPlace && !u.divDate

/** Записать семьи человека: новые завести, изменившиеся поправить,
 *  убранные отцепить. */
export async function saveUnions(
  ix: TreeIndex,
  person: string,
  list: Union[],
  was: Union[],
): Promise<void> {
  const male = ix.people.get(person)?.gender === 1
  const wasByHandle = new Map(was.filter(u => u.family).map(u => [u.family!, u]))
  const keep = new Set(list.map(u => u.family).filter(Boolean) as string[])

  for (const u of list) {
    if (!u.family) {
      if (unionIsEmpty(u)) continue
      const created = await apiPost<{handle?: string; _class?: string}[]>('/families/', {
        _class: 'Family',
        father_handle: male ? person : u.spouse,
        mother_handle: male ? u.spouse : person,
        child_ref_list: u.children.map(childRef),
      })
      const h = (created || []).find(i => i._class === 'Family')?.handle || created?.[0]?.handle
      if (!h) throw new Error('Сервер не вернул идентификатор новой семьи')
      if (u.wedDate || u.wedPlace) await setFamilyEvent(h, EVENT_MARRIAGE, u.wedDate, u.wedPlace)
      if (u.divDate || u.divPlace) await setFamilyEvent(h, EVENT_DIVORCE, u.divDate, u.divPlace)
      continue
    }

    const before = wasByHandle.get(u.family)
    const full = await apiGet<RawFamily>(`/families/${u.family}`)
    const personIsFather = full.father_handle === person
    const spouseChanged = (before?.spouse ?? null) !== u.spouse
    const kidsChanged = JSON.stringify(before?.children || []) !== JSON.stringify(u.children)

    if (spouseChanged || kidsChanged) {
      // ChildRef несёт вид родства (родной, приёмный) — сохраняем прежний
      const wasKids = new Map((full.child_ref_list || []).map(r => [r.ref, r]))
      await apiPut(`/families/${u.family}`, {
        ...full,
        ...(personIsFather ? {mother_handle: u.spouse} : {father_handle: u.spouse}),
        child_ref_list: u.children.map(c => wasKids.get(c) || childRef(c)),
      })
    }
    if (before?.wedDate !== u.wedDate || before?.wedPlace !== u.wedPlace) {
      await setFamilyEvent(u.family, EVENT_MARRIAGE, u.wedDate, u.wedPlace)
    }
    if (before?.divDate !== u.divDate || before?.divPlace !== u.divPlace) {
      await setFamilyEvent(u.family, EVENT_DIVORCE, u.divDate, u.divPlace)
    }
  }

  for (const h of wasByHandle.keys()) if (!keep.has(h)) await unlinkUnion(h, person)
}

/**
 * Отцепить человека от семьи. Саму семью сносим, только если после этого
 * в ней никого не осталось: у второго супруга и детей она своя, и стирать
 * её заодно нельзя.
 */
async function unlinkUnion(family: string, person: string): Promise<void> {
  const full = await apiGet<RawFamily>(`/families/${family}`)
  const next = {
    ...full,
    father_handle: full.father_handle === person ? null : full.father_handle || null,
    mother_handle: full.mother_handle === person ? null : full.mother_handle || null,
  }
  if (!next.father_handle && !next.mother_handle && !(full.child_ref_list || []).length) {
    await apiDelete(`/families/${family}`)
    return
  }
  await apiPut(`/families/${family}`, next)
}

/* ---------- события семьи: свадьба и развод ----------
 *
 * И то, и другое — события СЕМЬИ, а не человека: в его собственном списке
 * событий их нет вовсе. Развод семью НЕ распускает — супруги остаются в
 * ней, иначе вместе со связью пропало бы родительство детей. Разведённые
 * — это семья, у которой есть событие «Развод».
 */

export const EVENT_MARRIAGE = 1
export const EVENT_DIVORCE = 7

interface RawEventLite {
  handle: string
  type?: string | {value?: number}
  date?: {dateval?: number[]}
  place?: string | null
}

/** Тип приходит строкой на чтение и объектом на запись — принимаем оба. */
export const isFamilyEvent = (t: unknown, kind: number): boolean => {
  const name = kind === EVENT_DIVORCE ? 'Divorce' : 'Marriage'
  return typeof t === 'string' ? t === name : (t as {value?: number} | undefined)?.value === kind
}

/** Событие нужного вида у конкретной семьи, если оно есть. */
async function familyEventOf(family: string, kind: number): Promise<string | null> {
  const fam = await apiGet<RawFamily & {event_ref_list?: {ref: string}[]}>(`/families/${family}`)
  for (const r of fam.event_ref_list || []) {
    const e = await apiGet<RawEventLite>(`/events/${r.ref}`)
    if (isFamilyEvent(e.type, kind)) return e.handle
  }
  return null
}

/** Записать дату и место события семьи. Пустая дата без места убирает его. */
export async function setFamilyEvent(
  family: string,
  kind: number,
  date: string,
  place: string | null,
): Promise<void> {
  const event = await familyEventOf(family, kind)
  const dv = parseDate(date)
  const want = !!dv || !!place

  if (!want) {
    if (!event) return
    const fam = await apiGet<RawFamily & {event_ref_list?: {ref: string}[]}>(`/families/${family}`)
    await apiPut(`/families/${family}`, {
      ...fam,
      event_ref_list: (fam.event_ref_list || []).filter(r => r.ref !== event),
    })
    // событие могло быть общим — сносим, только если на нём больше никого
    const ev = await apiGet<Record<string, unknown>>(`/events/${event}`, {backlinks: 1})
    const bl = (ev.backlinks || {}) as Record<string, string[]>
    if (!Object.values(bl).flat().filter(x => x !== family).length) {
      await apiDelete(`/events/${event}`)
    }
    return
  }

  if (event) {
    const ev = await apiGet<Record<string, unknown>>(`/events/${event}`)
    await apiPut(`/events/${event}`, {...ev, ...bareEvent(kind, dv, place), handle: event})
    return
  }

  const h = await createObject('/events/', bareEvent(kind, dv, place), 'Event')
  const fam = await apiGet<RawFamily & {event_ref_list?: {ref: string}[]}>(`/families/${family}`)
  await apiPut(`/families/${family}`, {
    ...fam,
    event_ref_list: [...(fam.event_ref_list || []), eventRef(h)],
  })
}
